import { Component, DestroyRef, Inject, inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';

import { SiteService } from '@core/api/site.service';
import { FormatHelperService } from '@core/helper/format-helper.service';
import * as fromApp from '@store';
import { EnrollmentGroup, Site } from '@shared/models';
import { EnrollmentGroupService } from '@core/api/enrollment-group.service';
import { DialogYesNoComponent } from "../dialog-yes-no/dialog-yes-no.component";
import { setActiveSiteSuccess } from '@store/site/site.actions';

@Component({
  selector: 'fc-dialog-edit-custom-offer',
  templateUrl: './dialog-edit-custom-offer.component.html',
  styleUrl: './dialog-edit-custom-offer.component.scss',
  standalone: false
})
export class DialogEditCustomOfferComponent implements OnInit {
  destroyRef = inject(DestroyRef);
  updateCustomOfferForm: UntypedFormGroup;
  enrollmentGroups: EnrollmentGroup[] = [];
  customOffer: any;
  site: Site;

  constructor(
    readonly store: Store<fromApp.AppState>,
    readonly dialogRef: MatDialogRef<DialogEditCustomOfferComponent>,
    readonly dialog: MatDialog,
    readonly siteService: SiteService,
    readonly enrollmentGroupService: EnrollmentGroupService,
    public extraDialog: MatDialog,
    readonly formBuilder: UntypedFormBuilder,
    readonly formatHelper: FormatHelperService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.customOffer = data?.customOffer;
    this.site = data.site;
  }

  ngOnInit(): void {
    let filter = '?&sort=id(asc)&filters=is_active(eq,1)';
    if (this.site.submittedDate || this.site.originalSubmittedDate) {
      filter = `?filters=id(eq,${this.site.enrollmentGroupId})`;
    }
    this.enrollmentGroupService.getFilteredEnrollmentGroups(filter)
      .subscribe(groups => {
        this.enrollmentGroups = groups.results;
        this.updateCustomOfferForm = this.formBuilder.group({
          enrollmentGroupId: [this.customOffer?.id, Validators.required],
          bonusAmount: [this.customOffer?.bonusAmount || 0, Validators.required],
          bonusIsPpt: this.customOffer?.bonusIsPpt,
          bonusLength: this.customOffer?.bonusLength,
          initialFixedYears: [this.customOffer?.initialFixedYears, Validators.required],
          internalBufferValue: [this.customOffer?.internalBufferValue, [Validators.pattern(/^(?:0*(?:\.\d+)?|1(\.0*)?)$/)]],
          marketPricePercentage: [this.customOffer?.marketPricePercentage, [Validators.pattern(/^(?:0*(?:\.\d+)?|1(\.0*)?)$/)]],
          minimumErtPerAcre: [this.customOffer?.minimumErtPerAcre, Validators.required],
          minimumPaymentPerAcre: [this.customOffer?.minimumPaymentPerAcre, Validators.required],
          pricePerTon: [this.customOffer?.pricePerTon, Validators.required],
          pricePerTonMin: [this.customOffer?.pricePerTonMin || 0, Validators.required],
          faqs: this.customOffer?.faqs,
          termsUrl: this.customOffer?.termsUrl,
          comments: [null, Validators.required],
        });
        if (this.site.activationDate !== null) {
          this.updateCustomOfferForm.disable();
        }
        if (this.site.acceptedCustomTerms) {
          this.updateCustomOfferForm.controls.enrollmentGroupId.disable();
        }
      })
  }

  onNoClick(): void {
    this.dialogRef.close(null);
  }

  onSubmit() {

    const dialogRef = this.extraDialog.open(DialogYesNoComponent, {
      panelClass: 'fc-yes-no-dialog',
      data: {
        title: 'Confirmation',
        line1:
          'This custom offer will override any previous custom offers for this site and be immediately available for the landowner to select.',
        line2: 'This offer will only be valid as long as the BASE enrollment group remains active and the landowner is eligible to join.',
        line3: 'Do you wish to continue?',
        buttonTextYes: 'Yes, Continue',
        buttonTextNo: 'Cancel',
      },
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        let formValue = this.updateCustomOfferForm.getRawValue();
        let newCustomGroup = new EnrollmentGroup({
          id: formValue.enrollmentGroupId,
          isCustom: true,
          comments: formValue.comments,
          bonusAmount: this.formatHelper.removeCurrency(formValue.bonusAmount),
          bonusIsPpt: formValue.bonusIsPpt,
          bonusLength: formValue.bonusLength,
          initialFixedYears: formValue.initialFixedYears,
          internalBufferValue: formValue.internalBufferValue,
          marketPricePercentage: formValue.marketPricePercentage,
          minimumErtPerAcre: formValue.minimumErtPerAcre,
          minimumPaymentPerAcre: this.formatHelper.removeCurrency(formValue.minimumPaymentPerAcre),
          pricePerTon: this.formatHelper.removeCurrency(formValue.pricePerTon),
          pricePerTonMin: this.formatHelper.removeCurrency(formValue.pricePerTonMin),
          faqs: formValue.faqs,
          termsUrl: formValue.termsUrl,
        })
        return this.dialogRef.close(newCustomGroup);
      }
    });
  }

  changeBaseGroup(value: number) {
    let enrollmentGroup = this.enrollmentGroups.find(group => group.id === value);
    this.updateCustomOfferForm.patchValue({
      bonusAmount: enrollmentGroup.bonusAmount || 0,
      bonusIsPpt: enrollmentGroup.bonusIsPpt,
      bonusLength: enrollmentGroup.bonusLength,
      initialFixedYears: enrollmentGroup.initialFixedYears,
      internalBufferValue: enrollmentGroup.internalBufferValue,
      marketPricePercentage: enrollmentGroup.marketPricePercentage,
      minimumErtPerAcre: enrollmentGroup.minimumErtPerAcre,
      minimumPaymentPerAcre: enrollmentGroup.minimumPaymentPerAcre,
      pricePerTon: enrollmentGroup.pricePerTon,
      pricePerTonMin: enrollmentGroup.pricePerTonMin,
      faqs: enrollmentGroup.faqs,
      termsUrl: enrollmentGroup.termsUrl,
    });
  }

  deleteOffer() {
    if (this.site.activationDate !== null || this.site.acceptedCustomTerms) {
      return
    }
    const dialogRef = this.extraDialog.open(DialogYesNoComponent, {
      panelClass: 'fc-yes-no-dialog',
      data: {
        title: 'Confirmation',
        line1:
          'This will clear the sites custom offer and revert them back to the base enrollment group.',
        line3: 'Do you wish to continue?',
        buttonTextYes: 'Yes, Continue',
        buttonTextNo: 'Cancel',
      },
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.enrollmentGroupService.deleteCustomEnrollmentGroupForSite(this.site.id).subscribe(updatedSite => {
          this.store.dispatch(setActiveSiteSuccess({ data: updatedSite }));
          this.dialogRef.close();
        });
      }
    });
  }
}
